import * as THREE from 'three';
import {
  FeatureFace,
  FeatureLine,
  FeaturePoint,
  FeatureResultMesh,
  isFeatureFace,
  isFeatureLine,
  isFeaturePoint,
} from '../../mesh/feature';
import { buildTopologyEdgeRef, buildTopologyFaceRef, buildTopologyVertexRef } from '../../topology/TopologyReferenceBuilders';
import { resolveOwningFeatureResultMesh } from './resolveFeatureSceneMesh';

/**
 * 解析特征对象的元数据快照。
 *
 * @param object 拓扑代理对象或结果体。
 * @returns 元数据副本。
 */
function resolveFeatureMetadata(
  object: FeatureFace | FeatureLine | FeaturePoint | FeatureResultMesh,
): Record<string, unknown> {
  return object.getFeatureMetadata();
}

/**
 * 解析拓扑出处映射。
 *
 * @param metadata 特征元数据。
 * @returns 可用的出处映射；缺失时返回 `null`。
 */
function resolveTopologyProvenance(
  metadata: Record<string, unknown>,
): SerializedTopologyProvenanceMap | null {
  const candidate = metadata.topologyProvenance as SerializedTopologyProvenanceMap | undefined;
  if (!candidate || !Array.isArray(candidate.faces) || !Array.isArray(candidate.edges)) {
    return null;
  }
  return candidate;
}

/**
 * 解析特征拥有者 ID。
 *
 * @param metadata 特征元数据。
 * @returns 特征拥有者 ID。
 */
function resolveOwningFeatureId(metadata: Record<string, unknown>): string | undefined {
  return typeof metadata.featureId === 'string' && metadata.featureId.length > 0
    ? metadata.featureId
    : undefined;
}

/**
 * 解析当前子拓扑的贡献特征。
 *
 * 设计说明：结果体 metadata.featureId 描述当前几何宿主，不能代表每条保留边或面的生成者。
 * 选择层必须优先读取权威拓扑快照，其次读取持久化 provenance；只有旧数据完全缺失两者时
 * 才允许回退到宿主特征 ID。
 *
 * @param mesh 当前权威结果体。
 * @param kind 子拓扑种类。
 * @param topologyIndex 当前快照索引。
 * @param metadata 结果体元数据。
 * @returns 生成当前子拓扑的特征 ID；明确未知时返回 undefined。
 */
function resolveTopologyOwningFeatureId(
  mesh: FeatureResultMesh | null,
  kind: 'edge' | 'face' | 'vertex',
  topologyIndex: number,
  metadata: Record<string, unknown>,
): string | undefined {
  const snapshot = mesh?.getTopologySnapshot();
  if (kind === 'edge') {
    const edge = snapshot?.edges.find((candidate) => candidate.index === topologyIndex);
    if (edge) {
      return typeof edge.owningFeatureId === 'string' && edge.owningFeatureId.length > 0
        ? edge.owningFeatureId
        : undefined;
    }
  } else if (kind === 'face') {
    const face = snapshot?.faces.find((candidate) => candidate.index === topologyIndex);
    if (face) {
      return typeof face.owningFeatureId === 'string' && face.owningFeatureId.length > 0
        ? face.owningFeatureId
        : undefined;
    }
  }

  const provenance = resolveTopologyProvenance(metadata);
  const provenanceEntry = kind === 'edge'
    ? provenance?.edges.find((entry) => entry.edgeIndex === topologyIndex)
    : kind === 'face'
      ? provenance?.faces.find((entry) => entry.faceIndex === topologyIndex)
      : provenance?.vertices?.find((entry) => entry.vertexIndex === topologyIndex);
  if (provenanceEntry) {
    return typeof provenanceEntry.generatingFeatureId === 'string'
      && provenanceEntry.generatingFeatureId.length > 0
      ? provenanceEntry.generatingFeatureId
      : undefined;
  }
  return resolveOwningFeatureId(metadata);
}

/**
 * 将局部顶点坐标转换为序列化引用需要的元组。
 *
 * @param point 局部顶点坐标。
 * @returns 三维坐标元组。
 */
function toPointTuple(
  point: { x: number; y: number; z: number } | [number, number, number],
): [number, number, number] {
  if (Array.isArray(point)) {
    return [point[0], point[1], point[2]];
  }
  return [point.x, point.y, point.z];
}

/**
 * 解析当前对象所属的权威结果体。
 *
 * @param object 拓扑代理对象。
 * @returns 结果体；无法解析时返回 `null`。
 */
function resolveSelectionSourceMesh(
  object: FeatureFace | FeatureLine | FeaturePoint,
): FeatureResultMesh | null {
  return resolveOwningFeatureResultMesh(object);
}

/**
 * 解析面的持久化引用。
 *
 * @param mesh 所属结果体。
 * @param faceIndex 面索引。
 * @param fallbackPersistentRef 代理对象自带的持久化引用。
 * @returns 面持久化引用。
 */
function resolveFacePersistentRef(
  mesh: FeatureResultMesh | null,
  faceIndex: number,
  fallbackPersistentRef: string | null,
): string {
  if (fallbackPersistentRef) {
    return fallbackPersistentRef;
  }
  const snapshotFace = mesh?.getTopologySnapshot()?.faces.find((candidate) => candidate.index === faceIndex);
  if (snapshotFace?.ref) {
    return snapshotFace.ref;
  }
  const metadata = mesh ? resolveFeatureMetadata(mesh) : {};
  const provenance = resolveTopologyProvenance(metadata);
  return provenance?.faces.find((entry) => entry.faceIndex === faceIndex)?.ref
    ?? buildTopologyFaceRef(faceIndex);
}

/**
 * 构建面的运行时拓扑提示。
 *
 * @param bodyObjectId 所属结果体 ID。
 * @param faceIndex 面索引。
 * @param persistentRef 面持久化引用。
 * @param subEntityId 权威子拓扑 ID。
 * @returns 运行时拓扑提示。
 */
function buildFaceRuntimeTopologyHint(
  bodyObjectId: string,
  faceIndex: number,
  persistentRef: string,
  subEntityId?: string,
): RuntimeTopologyHint {
  return {
    kind: 'face',
    subEntityId,
    topologyIndex: faceIndex,
    sourceObjectId: bodyObjectId,
    persistentRef,
  };
}

/**
 * 解析边的相邻面提示。
 *
 * @param mesh 所属结果体。
 * @param adjacentFaceIndices 相邻面索引列表。
 * @param adjacentFaceRefs 代理对象自带的相邻面引用。
 * @returns 相邻面引用与提示集合。
 */
function resolveAdjacentFacePayload(
  mesh: FeatureResultMesh | null,
  adjacentFaceIndices: number[],
  adjacentFaceRefs: string[],
): {
  adjacentFaceRefs: string[];
  adjacentFaceHints: RuntimeTopologyFaceHint[];
} {
  if (!mesh) {
    return {
      adjacentFaceRefs: [...adjacentFaceRefs],
      adjacentFaceHints: adjacentFaceRefs.map((persistentRef, index) => ({
        topologyIndex: adjacentFaceIndices[index] ?? index,
        persistentRef,
      })),
    };
  }
  const metadata = resolveFeatureMetadata(mesh);
  const provenance = resolveTopologyProvenance(metadata);
  const resolvedRefs = adjacentFaceIndices.map((faceIndex) => {
    const snapshotFace = mesh.getTopologySnapshot()?.faces.find((candidate) => candidate.index === faceIndex);
    return snapshotFace?.ref
      ?? provenance?.faces.find((entry) => entry.faceIndex === faceIndex)?.ref
      ?? buildTopologyFaceRef(faceIndex);
  });
  const resolvedHints = adjacentFaceIndices.map((faceIndex) => {
    const snapshotFace = mesh.getTopologySnapshot()?.faces.find((candidate) => candidate.index === faceIndex);
    const matchedFace = provenance?.faces.find((entry) => entry.faceIndex === faceIndex);
    return {
      subEntityId: snapshotFace?.subEntityId ?? matchedFace?.subEntityId,
      topologyIndex: faceIndex,
      persistentRef: snapshotFace?.ref ?? matchedFace?.ref ?? buildTopologyFaceRef(faceIndex),
      shapeHash: undefined,
    };
  });
  return {
    adjacentFaceRefs: resolvedRefs,
    adjacentFaceHints: resolvedHints,
  };
}

/**
 * 解析边的持久化引用。
 *
 * @param mesh 所属结果体。
 * @param edgeIndex 边索引。
 * @param fallbackPersistentRef 代理对象自带的持久化引用。
 * @returns 边持久化引用；无法解析时返回 `null`。
 */
function resolveEdgePersistentRef(
  mesh: FeatureResultMesh | null,
  edgeIndex: number,
  fallbackPersistentRef: string | null,
): string | null {
  if (fallbackPersistentRef) {
    return fallbackPersistentRef;
  }
  const snapshot = mesh?.getTopologySnapshot();
  const edge = snapshot?.edges.find((candidate) => candidate.index === edgeIndex);
  const vertexA = typeof edge?.vertexAIndex === 'number'
    ? snapshot?.vertices[edge.vertexAIndex]
    : null;
  const vertexB = typeof edge?.vertexBIndex === 'number'
    ? snapshot?.vertices[edge.vertexBIndex]
    : null;
  if (!edge || !vertexA || !vertexB) {
    const metadata = mesh ? resolveFeatureMetadata(mesh) : {};
    const provenance = resolveTopologyProvenance(metadata);
    const byProvenance = provenance?.edges.find((entry) => entry.edgeIndex === edgeIndex)?.ref;
    return typeof byProvenance === 'string' && byProvenance.length > 0
      ? byProvenance
      : null;
  }
  if (edge.ref) {
    return edge.ref;
  }
  return buildTopologyEdgeRef(
    toPointTuple(vertexA.position),
    toPointTuple(vertexB.position),
  );
}

/**
 * 解析边的语义拓扑名。
 *
 * 设计说明：语义名只存在于权威拓扑快照，代理对象不携带；新点选必须在这里回读，
 * 否则语义身份写不进特征命令，上游尺寸变化后只能退回会漂移的坐标引用。
 *
 * @param mesh 所属结果体。
 * @param edgeIndex 边索引。
 * @returns 语义边名；快照未提供时返回 `undefined`。
 */
function resolveEdgeSemanticName(
  mesh: FeatureResultMesh | null,
  edgeIndex: number,
): string | undefined {
  const edge = mesh?.getTopologySnapshot()?.edges.find((candidate) => candidate.index === edgeIndex);
  return edge?.semanticName ?? undefined;
}

/**
 * 构建边的运行时拓扑提示。
 *
 * @param bodyObjectId 所属结果体 ID。
 * @param edgeIndex 边索引。
 * @param persistentRef 边持久化引用。
 * @param subEntityId 权威子拓扑 ID。
 * @param adjacentFaceRefs 相邻面持久化引用。
 * @param adjacentFaceHints 相邻面运行时提示。
 * @returns 运行时拓扑提示。
 */
function buildEdgeRuntimeTopologyHint(
  bodyObjectId: string,
  edgeIndex: number,
  persistentRef: string,
  subEntityId: string | undefined,
  adjacentFaceRefs: string[],
  adjacentFaceHints: RuntimeTopologyFaceHint[],
): RuntimeTopologyHint {
  return {
    kind: 'edge',
    subEntityId,
    topologyIndex: edgeIndex,
    sourceObjectId: bodyObjectId,
    persistentRef,
    adjacentFaceRefs,
    adjacentFaceHints,
  };
}

/**
 * 解析顶点的持久化引用。
 *
 * @param mesh 所属结果体。
 * @param vertexIndex 顶点索引。
 * @param fallbackPersistentRef 代理对象自带的持久化引用。
 * @param fallbackPosition 代理对象自身的位置。
 * @returns 顶点持久化引用；无法解析时返回 `null`。
 */
function resolveVertexPersistentRef(
  mesh: FeatureResultMesh | null,
  vertexIndex: number,
  fallbackPersistentRef: string | null,
  fallbackPosition: THREE.Vector3,
): string | null {
  if (fallbackPersistentRef) {
    return fallbackPersistentRef;
  }
  const vertex = mesh?.getTopologySnapshot()?.vertices[vertexIndex];
  if (vertex) {
    return vertex.ref ?? buildTopologyVertexRef(toPointTuple(vertex.position));
  }
  return buildTopologyVertexRef([fallbackPosition.x, fallbackPosition.y, fallbackPosition.z]);
}

/**
 * 构建顶点的运行时拓扑提示。
 *
 * @param bodyObjectId 所属结果体 ID。
 * @param vertexIndex 顶点索引。
 * @param persistentRef 顶点持久化引用。
 * @param subEntityId 权威子拓扑 ID。
 * @returns 运行时拓扑提示。
 */
function buildVertexRuntimeTopologyHint(
  bodyObjectId: string,
  vertexIndex: number,
  persistentRef: string,
  subEntityId?: string,
): RuntimeTopologyHint {
  return {
    kind: 'vertex',
    subEntityId,
    topologyIndex: vertexIndex,
    sourceObjectId: bodyObjectId,
    persistentRef,
  };
}

/**
 * 将拓扑代理对象序列化为权威选择快照。
 *
 * 设计说明：
 * 1. 该函数把 `Object3D` 层的命中结果立即收口为可序列化的拓扑真相，避免上层继续从场景对象反推；
 * 2. 运行时提示、相邻面引用和持久化引用在这里一次性补齐，保证事件层、宿主层和 solver 层共享同一份选择语义；
 * 3. 若当前对象缺少构建权威快照所需的最小上下文，则返回 `null`，由上层显式识别为“非拓扑选择”。
 *
 * @param object 真实命中的拓扑代理对象。
 * @returns 权威拓扑命中快照；无法序列化时返回 `null`。
 */
export function resolveTopologySelectionPick(
  object: THREE.Object3D | null | undefined,
): ResolvedTopologyPick | null {
  if (!object) {
    return null;
  }

  if (isFeatureLine(object) && typeof object.edgeIndex === 'number' && object.objectId) {
    const mesh = resolveSelectionSourceMesh(object);
    const persistentRef = resolveEdgePersistentRef(mesh, object.edgeIndex, object.persistentRef);
    if (!persistentRef) {
      return null;
    }
    const adjacentFacePayload = resolveAdjacentFacePayload(
      mesh,
      object.adjacentFaceIndices,
      object.adjacentFaceRefs,
    );
    const metadata = resolveFeatureMetadata(mesh ?? object);
    return {
      sceneObjectId: object.uuid,
      kind: 'edge',
      bodyObjectId: object.objectId,
      topologyIndex: object.edgeIndex,
      subEntityId: object.subEntityId ?? undefined,
      persistentRef,
      owningFeatureId: resolveTopologyOwningFeatureId(
        mesh,
        'edge',
        object.edgeIndex,
        metadata,
      ),
      semanticName: resolveEdgeSemanticName(mesh, object.edgeIndex),
      adjacentFaceRefs: adjacentFacePayload.adjacentFaceRefs,
      runtimeTopologyHint: buildEdgeRuntimeTopologyHint(
        object.objectId,
        object.edgeIndex,
        persistentRef,
        object.subEntityId ?? undefined,
        adjacentFacePayload.adjacentFaceRefs,
        adjacentFacePayload.adjacentFaceHints,
      ),
    };
  }

  if (isFeatureFace(object) && object.objectId && typeof object.faceIndex === 'number') {
    const mesh = resolveSelectionSourceMesh(object);
    const persistentRef = resolveFacePersistentRef(mesh, object.faceIndex, object.persistentRef);
    const metadata = resolveFeatureMetadata(mesh ?? object);
    return {
      sceneObjectId: object.uuid,
      kind: 'face',
      bodyObjectId: object.objectId,
      topologyIndex: object.faceIndex,
      subEntityId: object.subEntityId,
      persistentRef,
      owningFeatureId: resolveTopologyOwningFeatureId(
        mesh,
        'face',
        object.faceIndex,
        metadata,
      ),
      runtimeTopologyHint: buildFaceRuntimeTopologyHint(
        object.objectId,
        object.faceIndex,
        persistentRef,
        object.subEntityId,
      ),
    };
  }

  if (isFeaturePoint(object) && typeof object.vertexIndex === 'number' && object.objectId) {
    const mesh = resolveSelectionSourceMesh(object);
    const persistentRef = resolveVertexPersistentRef(
      mesh,
      object.vertexIndex,
      object.persistentRef,
      object.position,
    );
    if (!persistentRef) {
      return null;
    }
    const metadata = resolveFeatureMetadata(mesh ?? object);
    return {
      sceneObjectId: object.uuid,
      kind: 'vertex',
      bodyObjectId: object.objectId,
      topologyIndex: object.vertexIndex,
      subEntityId: object.subEntityId ?? undefined,
      persistentRef,
      owningFeatureId: resolveTopologyOwningFeatureId(
        mesh,
        'vertex',
        object.vertexIndex,
        metadata,
      ),
      runtimeTopologyHint: buildVertexRuntimeTopologyHint(
        object.objectId,
        object.vertexIndex,
        persistentRef,
        object.subEntityId ?? undefined,
      ),
    };
  }

  return null;
}
